import i18n from "@helpers/lib/i18n";
import { interpolateString } from "@helpers/string-utils";
import type {
	ConfiguredDialogSchemaType,
	WelcomeDialogSchemaType,
} from "@memorang/constants/config/schema";
import {
	formatCount,
	formatSecondsToDurationLabel,
} from "@memorang/helpers/common";
import type { BaseItemSchema, SessionType } from "@memorang/types";

export type DialogType =
	| "quit"
	| "completeSection"
	| "completePart"
	| "completeTest"
	| "timerExpired"
	| "itemTimeExpired";

const getAverageTimePerQuestion = (
	totalTimeSeconds: number,
	numTotalQuestions: number,
) =>
	formatSecondsToDurationLabel(
		Math.round(totalTimeSeconds / numTotalQuestions),
		true,
	);

export const getBreakDialogConfig = (breakSeconds: number) => {
	const breakMinutes = Math.floor(breakSeconds / 60);
	return {
		title: "Would you like to take a break?",
		icon: "timer-sand-paused",
		content: `You’ve been focused for a while. A **${breakMinutes}-minute** break can help you stay sharp. Your exam timer will pause and you can continue when you feel ready.`,
		buttons: [
			{
				label: "Continue exam",
				type: "cancel" as const,
			},
			{
				label: "Start break",
				type: "confirm" as const,
			},
		],
	};
};

export const getBreakExpiredDialogConfig = () => {
	return {
		title: "Break Time Expired",
		icon: "timer-sand-complete",
		content:
			"Your break time has expired, and the test has automatically resumed. You can now continue with your test.",
		buttons: [
			{
				label: "Continue",
				type: "confirm" as const,
			},
		],
	};
};

export const getBreakOngoingDialogConfig = () => {
	return {
		title: "You're on a scheduled break",
		icon: "pause",
		content: `Your exam will resume automatically when the break ends. You may return early if ready.  
		Please do not refresh or leave this page.`,
		buttons: [
			{
				label: "Resume exam",
				type: "confirm" as const,
			},
		],
	};
};

export const constructExamResourcesMarkdown = (links: string[]) => {
	const linksMarkdown = links.map((link) => `- ${link}`).join("\n");
	return `#### Study Resources\n\n${linksMarkdown}`;
};
export const helpDialog = ({
	sessionItems,
	totalTime,
	examName,
	isDiagnosticTest,
	numSections,
	numTotalQuestions,
	diagnosticLabel,
	studyResourcesMarkdown,
	helpDialogConfig,
	hasListening,
}: {
	sessionItems: BaseItemSchema[];
	totalTime: number;
	examName: string;
	isDiagnosticTest: boolean;
	numSections: number;
	numTotalQuestions: number;
	diagnosticLabel?: Record<string, string>;
	studyResourcesMarkdown?: string;
	helpDialogConfig?: ConfiguredDialogSchemaType;
	hasListening?: boolean;
}) => {
	if (helpDialogConfig) {
		const skillValues = getSkillValues(
			helpDialogConfig.skillVariables,
			hasListening,
		);
		return {
			triggerButton: { label: "Help", icon: "help-circle-outline" },
			title: interpolateString(helpDialogConfig.title, skillValues),
			icon: "help-circle-outline",
			body: interpolateString(
				interpolateString(helpDialogConfig.body, skillValues),
				{ min: String(Math.round(totalTime / 60)) },
			),
			confirmCta: "Close",
		};
	}
	const numQuestions = sessionItems.length;
	const totalTimeLabel = formatSecondsToDurationLabel(totalTime);
	const averageTimePerQuestion = getAverageTimePerQuestion(
		totalTime,
		numTotalQuestions,
	);

	const overviewLines = [
		isDiagnosticTest
			? `* You're taking a ${diagnosticLabel ? diagnosticLabel[i18n.language] : "Diagnostic"} test.`
			: `* The exam is the ${examName}, with ${numTotalQuestions} items.`,
		numSections > 1
			? `* This test has ${numSections} sections of ${numQuestions} questions each.\n  * There are a total of ${numTotalQuestions} questions.`
			: null,
		"* After finishing, you'll receive a detailed score report.",
	]
		.filter(Boolean)
		.join("\n  ");

	const navigationLine =
		numSections > 1
			? "You can freely navigate within a section, but once you move to the next section you cannot go back."
			: "You can freely jump to any question.";

	return {
		triggerButton: { label: "Help", icon: "help-circle-outline" },
		title: "Help",
		icon: "help-circle-outline",
		body: `
  ${studyResourcesMarkdown ? `${studyResourcesMarkdown}\n\n` : ""}#### Overview
  ${overviewLines}
  #### Time
  * Total time: ${totalTimeLabel}.
  * Average: ~${averageTimePerQuestion} per question.
  * Remaining time is always shown in the top right.
  #### Answering Questions
  * Select the best answer.
  * Right-click an option to cross it out.
  #### Navigation
  * Use **Next**, **Prev**, or **Skip** to move between questions.
  * ${navigationLine}
  #### Tools
  * **Highlighter:** Available in the top-left toolbar. Use it to highlight text in the question stem.
  * **Cross Out:** Right-click an answer to mark it as eliminated.
  `,
		confirmCta: "Close",
	};
};

const buildQuitContent = ({
	timeRemainingSeconds,
	numItemsLeft,
}: {
	timeRemainingSeconds?: number;
	numItemsLeft?: number;
}) => {
	const clauses: string[] = [];
	if (timeRemainingSeconds && timeRemainingSeconds > 0) {
		clauses.push(
			`**${formatSecondsToDurationLabel(timeRemainingSeconds, true)}** remaining`,
		);
	}
	if (numItemsLeft && numItemsLeft > 0) {
		clauses.push(
			`**${formatCount("unanswered question", numItemsLeft, true)}**`,
		);
	}
	const status =
		clauses.length > 0 ? ` You have ${clauses.join(" and ")}.` : "";
	return `Are you sure you want to pause this test?${status} You can always resume later.`;
};

export const getDialogSessionConfigs = ({
	currentSection,
	numTotalSections,
	currentPart,
	numTotalParts,
	nextPartName,
	timeRemainingSeconds,
	numItemsLeft,
	itemTimeLimitSeconds,
	isLastQuestion,
}: {
	currentSection?: number;
	numTotalSections?: number;
	currentPart?: number;
	numTotalParts?: number;
	nextPartName?: string;
	timeRemainingSeconds?: number;
	numItemsLeft?: number;
	itemTimeLimitSeconds?: number;
	isLastQuestion?: boolean;
}): Record<
	DialogType,
	{
		title: string;
		icon: string;
		content: string;
		buttons?: {
			label: string;
			type: "confirm" | "cancel";
		}[];
	}
> => ({
	quit: {
		title: "Pause Test",
		icon: "pause-circle-outline",
		content: buildQuitContent({ timeRemainingSeconds, numItemsLeft }),
		buttons: [
			{
				label: "Cancel",
				type: "cancel",
			},
			{
				label: "Pause",
				type: "confirm",
			},
		],
	},
	completeSection: {
		title: "Complete Section",
		icon: "check-outline",
		content:
			numTotalSections && currentSection
				? `You have completed **section ${currentSection} of ${numTotalSections}**. Once you move on to the **next section** you will not be able to go back to view or review your answers.`
				: "You have completed all the items in this section. Once you move on to the **next section** you will not be able to go back to view or review your answers.",
		buttons: [
			{
				label: "Cancel",
				type: "cancel",
			},
			{
				label: "Next Section",
				type: "confirm",
			},
		],
	},
	completePart: {
		title: "Complete Part",
		icon: "check-outline",
		content:
			numTotalParts && currentPart
				? `You have completed **Part ${currentPart} of ${numTotalParts}**${
						nextPartName ? `. Up next: **${nextPartName}**` : ""
					}. Once you move on to the **next part** you will not be able to go back to view or review your answers.`
				: "You have completed this part. Once you move on to the **next part** you will not be able to go back to view or review your answers.",
		buttons: [
			{
				label: "Cancel",
				type: "cancel",
			},
			{
				label: "Next Part",
				type: "confirm",
			},
		],
	},
	completeTest: {
		title: "Finish Test",
		icon: "flag-checkered",
		content:
			"You have attempted all items in this practice test. Click **finish** to end your test and see your results.",
		buttons: [
			{
				label: "Cancel",
				type: "cancel",
			},
			{
				label: "Finish",
				type: "confirm",
			},
		],
	},
	timerExpired: {
		title: "Time Expired",
		icon: "timer-sand-complete",
		content: "Your time has expired and your test will now be submitted.",
	},
	itemTimeExpired: {
		title: "Time Expired",
		icon: "timer-sand-complete",
		content: `${
			itemTimeLimitSeconds
				? `You've used the full **${formatSecondsToDurationLabel(itemTimeLimitSeconds, true)}** allotted for this question.`
				: "Your time for this question has expired."
		} ${
			isLastQuestion
				? "You will now be taken to your score report."
				: "You will now move on to the next question."
		}`,
		buttons: [
			{
				label: isLastQuestion ? "View Report" : "Next Question",
				type: "confirm",
			},
		],
	},
});

const buildQuizContent = ({
	diagnosticLabel,
	topic,
	totalTimeLabel,
	avgPerQuestion,
}: {
	diagnosticLabel?: Record<string, string>;
	topic: string;
	totalTimeLabel: string;
	avgPerQuestion: string;
}) => {
	const label = diagnosticLabel
		? diagnosticLabel[i18n.language]
		: "**diagnostic assessment**";
	return [
		`Welcome to your ${label} on the topic of **${topic}**.`,
		`- The total time allotted to complete the exam is **${totalTimeLabel}**.`,
		`- You have an average of **${avgPerQuestion}** per question.`,
		"- Feel free to navigate between questions as you work through them.",
		"- Once you have completed the test you will receive a score report with an analysis of your results. Good luck!",
	].join("\n");
};

const buildFullLengthContent = ({
	examName,
	numSections,
	numTotalQuestions,
	totalTimeLabel,
	avgPerQuestion,
	disclaimer,
}: {
	examName: string;
	numSections: number;
	numTotalQuestions: number;
	totalTimeLabel: string;
	avgPerQuestion: string;
	disclaimer?: string;
}) => {
	const questionsIntro =
		numSections > 1
			? `**${formatCount("section", numSections, true)}** and a total of **${numTotalQuestions} questions**`
			: `**${numTotalQuestions} questions**`;
	const navigationNote =
		numSections > 1
			? " within a section, but once you move to the next section you cannot go back"
			: "";

	return [
		`In this **${examName}**, you will be tasked with completing ${questionsIntro}.`,
		`- You can freely navigate between questions${navigationNote}.`,
		`- The total time allotted to complete the exam is **${totalTimeLabel}**.`,
		`- You have an average of **${avgPerQuestion}** per question.`,
		"- Once you have completed the test you will receive a score report with an analysis of your results. Good luck!",
		disclaimer ? disclaimer : null,
	]
		.filter(Boolean)
		.join("\n");
};

const getSkillValues = (
	skillVariables: Record<string, Record<string, string>> | undefined,
	hasListening?: boolean,
): Record<string, string> => {
	if (!skillVariables) {
		return {};
	}
	const skill = hasListening ? "listening" : "reading";
	const values: Record<string, string> = {};
	for (const [key, variants] of Object.entries(skillVariables)) {
		if (variants[skill] !== undefined) {
			values[key] = variants[skill];
		}
	}
	return values;
};

export const getSessionStartDialogConfig = ({
	examName,
	totalTime,
	isQuiz,
	numSections,
	numTotalQuestions,
	diagnosticLabel,
	welcomeDialog,
	hasListening,
}: {
	examName: string;
	totalTime: number;
	isQuiz?: boolean;
	numSections: number;
	numTotalQuestions: number;
	diagnosticLabel?: Record<string, string>;
	welcomeDialog?: WelcomeDialogSchemaType;
	hasListening?: boolean;
}) => {
	const totalTimeLabel = formatSecondsToDurationLabel(totalTime);
	const avgPerQuestion = getAverageTimePerQuestion(
		totalTime,
		numTotalQuestions,
	);
	const topic = examName.includes(": ") ? examName.split(": ")[1] : examName;
	const skillValues = getSkillValues(
		welcomeDialog?.diagnostic.skillVariables,
		hasListening,
	);

	const title =
		isQuiz && welcomeDialog?.diagnostic.title
			? interpolateString(
					interpolateString(welcomeDialog.diagnostic.title, skillValues),
					{ examName },
				)
			: examName;

	const content = isQuiz
		? welcomeDialog?.diagnostic.body
			? interpolateString(
					interpolateString(welcomeDialog.diagnostic.body, skillValues),
					{
						min: String(Math.round(totalTime / 60)),
						sec: avgPerQuestion,
					},
				)
			: buildQuizContent({
					diagnosticLabel,
					topic,
					totalTimeLabel,
					avgPerQuestion,
				})
		: buildFullLengthContent({
				examName,
				numSections,
				numTotalQuestions,
				totalTimeLabel,
				avgPerQuestion,
				disclaimer: welcomeDialog?.disclaimer,
			});

	return {
		title,
		icon: "clipboard-check-outline",
		content,
		buttons: [{ label: "Continue", type: "confirm" as const }],
	};
};

export const getExitDialogConfig = ({
	isOlx,
}: { isOlx?: boolean }): Record<
	SessionType,
	{
		title: string;
		body: string;
		dismissCta: string;
		confirmCta: string;
	}
> => ({
	FORMATIVE: {
		title: "End session?",
		body: "Your progress will be saved. You can start a new session later to continue practicing.",
		dismissCta: "Exit",
		confirmCta: "Continue practicing",
	},
	SUMMATIVE: isOlx
		? {
				title: "Exit while timer runs?",
				body: "Your timer will continue running even if you leave this page.",
				dismissCta: "Exit anyway",
				confirmCta: "Stay",
			}
		: {
				title: "Pause assessment?",
				body: "Your timer will pause, and you can resume this assessment anytime.",
				dismissCta: "Pause & exit",
				confirmCta: "Continue",
			},
});
